import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { createKartverketTopoLayer } from '../lib/mapTiles';
import {
  geoJsonPolygonToRing,
  locksInsidePolygon,
  parseMapPolygon,
  ringToLeafletLatLngs
} from '../lib/geo';
import { dataverseService } from '../services/dataverseService';
import { getDataverseErrorMessage } from '../lib/dataverseErrorHandler';

function hasCoordinates(lock) {
  return typeof lock.latitude === 'number' && typeof lock.longitude === 'number';
}

function destroyLeafletMap(mapRef) {
  const map = mapRef.current;
  if (!map) {
    return;
  }
  mapRef.current = null;
  try {
    map.stop();
    map.off();
    map.remove();
  } catch {
    // Dialog may already have unmounted the container.
  }
}

/**
 * View stored map zone and optionally add locks inside the polygon.
 */
export default function AccessGroupZoneDialog({
  open,
  onClose,
  group,
  locks = [],
  center,
  onUpdated
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const mapPolygon = useMemo(() => parseMapPolygon(group?.mapPolygon), [group]);

  const polygonKey = useMemo(
    () => (mapPolygon ? JSON.stringify(mapPolygon) : ''),
    [mapPolygon]
  );

  const memberIds = useMemo(
    () => new Set((group?.locks || []).map((lock) => lock.id || lock.lockId)),
    [group]
  );

  const mappableLocks = useMemo(
    () => locks.filter((lock) => hasCoordinates(lock)),
    [locks]
  );

  const locksInZone = useMemo(
    () => locksInsidePolygon(mappableLocks, mapPolygon),
    [mappableLocks, mapPolygon]
  );

  const newLocks = useMemo(
    () => locksInZone.filter((lock) => !memberIds.has(lock.id || lock.lockId)),
    [locksInZone, memberIds]
  );

  const handleClose = useCallback(() => {
    destroyLeafletMap(mapRef);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      destroyLeafletMap(mapRef);
      return undefined;
    }

    if (!mapPolygon) {
      return undefined;
    }

    setError(null);
    setMessage(null);
    setSaving(false);

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled || !mapContainerRef.current) {
        return;
      }

      destroyLeafletMap(mapRef);

      const ring = geoJsonPolygonToRing(mapPolygon);
      const latLngs = ringToLeafletLatLngs(ring);
      if (!latLngs) {
        setError('Kart-sonen mangler gyldig geometri.');
        return;
      }

      const map = L.map(mapContainerRef.current, { scrollWheelZoom: true });
      createKartverketTopoLayer().addTo(map);

      const polygonLayer = L.polygon(latLngs, {
        color: '#0d47a1',
        weight: 3,
        opacity: 1,
        fillColor: '#1976d2',
        fillOpacity: 0.28,
        interactive: false
      }).addTo(map);

      mappableLocks.forEach((lock) => {
        const isMember = memberIds.has(lock.id || lock.lockId);
        const inZone = locksInZone.some(
          (entry) => (entry.id || entry.lockId) === (lock.id || lock.lockId)
        );
        const color = isMember ? '#0d47a1' : inZone ? '#ed6c02' : '#2e7d32';
        L.circleMarker([lock.latitude, lock.longitude], {
          radius: isMember ? 9 : 7,
          color,
          weight: 2,
          fillColor: color,
          fillOpacity: 0.9
        })
          .bindTooltip(
            `${lock.name}${isMember ? ' (medlem)' : inZone ? ' (i sone)' : ''}`,
            { direction: 'top', offset: [0, -8] }
          )
          .addTo(map);
      });

      const bounds = polygonLayer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [48, 48], maxZoom: 16 });
      } else if (center) {
        map.setView([center.lat, center.lng], 12);
      }

      mapRef.current = map;
      requestAnimationFrame(() => {
        if (mapRef.current === map) {
          map.invalidateSize();
          polygonLayer.bringToFront();
        }
      });
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      destroyLeafletMap(mapRef);
    };
    // Intentionally only re-init when dialog opens / polygon changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, polygonKey, group?.id]);

  const handleUpdateFromZone = async () => {
    if (!group?.id) {
      return;
    }
    if (newLocks.length === 0) {
      setMessage('Ingen nye låser i sonen.');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const nextLockIds = [
        ...memberIds,
        ...newLocks.map((lock) => lock.id || lock.lockId)
      ];
      const phoneIds = (group.phones || []).map((phone) => phone.id || phone.phoneId);
      await dataverseService.accessGroupService.setMembership(group.id, {
        lockIds: [...new Set(nextLockIds)],
        phoneIds
      });
      setMessage(`La til ${newLocks.length} lås(er) fra sonen.`);
      onUpdated?.();
    } catch (updateError) {
      setError(getDataverseErrorMessage(updateError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableRestoreFocus
      TransitionProps={{
        onExited: () => destroyLeafletMap(mapRef)
      }}
    >
      <DialogTitle>Kart-sone: {group?.name}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {locksInZone.length} Unloc-lås(er) inne i sonen · {newLocks.length} nye (ikke medlem ennå)
          </Typography>
          {!mapPolygon && (
            <Alert severity="warning">Ingen lagret kart-sone på denne gruppen.</Alert>
          )}
          <Box
            ref={mapContainerRef}
            sx={{
              height: { xs: 360, md: 480 },
              width: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.100'
            }}
          />
          {newLocks.length > 0 && (
            <Box
              sx={{
                maxHeight: 120,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Nye låser som kan legges til
              </Typography>
              {newLocks.map((lock) => (
                <Typography key={lock.id || lock.lockId} variant="body2">
                  {lock.name}
                </Typography>
              ))}
            </Box>
          )}
          {message && <Alert severity="success">{message}</Alert>}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Lukk
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdateFromZone}
          disabled={saving || newLocks.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Oppdaterer…' : `Oppdater fra sone${newLocks.length ? ` (+${newLocks.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
